import { useState, useEffect, useCallback } from 'react';

export interface AuthUser {
  email: string;
  name?: string;
  picture?: string;
}

export type AuthState =
  | { status: 'loading' }
  | { status: 'open' } // SSO disabled server-side — no login required (localhost dev)
  | { status: 'anon'; clientId: string; domain: string } // SSO on, not signed in
  | { status: 'authed'; user: AuthUser }
  | { status: 'error' }; // could not reach /api/auth/config — fail closed (retry)

/**
 * Decides whether the login page or the dashboard is shown.
 * The backend gate is the real enforcement; this only drives the UI. We fail
 * CLOSED: 'open' is set only when /api/auth/config returns a definitive 200 with
 * enabled:false. Any fetch/parse error → 'error' (retry screen), never 'open',
 * so a transient blip or a MITM'd config response can't flash an open dashboard.
 */
export function useAuth() {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/config');
      if (!res.ok) {
        setState({ status: 'error' });
        return;
      }
      const cfg = await res.json();
      if (!cfg?.enabled) {
        setState({ status: 'open' });
        return;
      }
      const me = await fetch('/api/auth/me');
      if (me.ok) {
        const { user } = await me.json();
        setState({ status: 'authed', user });
      } else {
        setState({ status: 'anon', clientId: cfg.clientId, domain: cfg.domain ?? 'zop.dev' });
      }
    } catch {
      setState({ status: 'error' });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
      // Tell Google to forget the auto-select choice so the next login is explicit.
      (window as any).google?.accounts?.id?.disableAutoSelect?.();
    } catch {
      /* ignore */
    }
    refresh();
  }, [refresh]);

  return { state, refresh, logout };
}
